import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { TabsNavigationProps } from "./Tabs.type";
// @ts-ignore
import styles from "./Tabs.module.scss";

const TabsNavigation = ({
  content,
  pendingTab,
  activeTab,
  handleTabClick,
  button_style,
  paddingTop,
  paddingBottom,
  tab_button_size = 'large'
}: TabsNavigationProps) => {
  // Helper function to extract translateX value from transform string
  const getTranslateXValue = useCallback((element: HTMLElement): number => {
    const style = window.getComputedStyle(element);
    const transform = style.transform;
    if (transform === 'none') return 0;

    const matrix = new DOMMatrix(transform);
    return matrix.m41; // translateX value
  }, []);
  const [gliderStyle, setGliderStyle] = useState<{ width: number; left: number }>({
    width: 0,
    left: 0
  });
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragStarted: false,
    startX: 0,
    startLeft: 0,
    showLeftFade: false,
    showRightFade: false,
    canDrag: false
  });

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const navListRef = useRef<HTMLDivElement | null>(null);
  const tabWrapperRef = useRef<HTMLUListElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const justDragged = useRef(false);
  const DRAG_THRESHOLD = 5;

  const updateGlider = useCallback(() => {
    const activeBtn = buttonRefs.current[activeTab];
    const wrapper = tabWrapperRef.current;

    if (activeBtn && wrapper) {
      const btnRect = activeBtn.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      setGliderStyle({
        width: btnRect.width,
        left: btnRect.left - wrapperRect.left,
      });
    }
  }, [activeTab]);

  const updateFadeState = useCallback(() => {
    const wrapper = tabWrapperRef.current;
    const container = navListRef.current;

    if (!wrapper || !container) return;

    const currentLeft = getTranslateXValue(wrapper);
    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const maxLeft = -(wrapperWidth - availableWidth);

    setDragState(prev => ({
      ...prev,
      showLeftFade: currentLeft < 0,
      showRightFade: currentLeft > maxLeft
    }));
  }, [getTranslateXValue]);

  const checkOverflow = useCallback(() => {
    const container = navListRef.current;
    const wrapper = tabWrapperRef.current;

    if (!container || !wrapper) return;

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;

    const canDrag = wrapperWidth > availableWidth;

    setDragState(prev => ({
      ...prev,
      canDrag,
      showLeftFade: canDrag ? prev.showLeftFade : false,
      showRightFade: canDrag ? prev.showRightFade : false
    }));

    if (canDrag) {
      updateFadeState();
    }
  }, [updateFadeState]);

  const handleResize = useCallback(() => {
    const wrapper = tabWrapperRef.current;
    const container = navListRef.current;

    if (!wrapper || !container) return;

    const currentLeft = getTranslateXValue(wrapper);

    checkOverflow();

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const canDrag = wrapperWidth > availableWidth;

    if (canDrag) {
      const maxLeft = -(wrapperWidth - availableWidth);
      const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);

      // If current position would show empty space, adjust it
      if (currentLeft + wrapperGap < maxLeft) {
        wrapper.classList.add(styles.smooth);
        wrapper.style.transform = `translateX(${maxLeft}px)`;
        updateFadeState();

        // Remove smooth class after animation
        setTimeout(() => {
          wrapper.classList.remove(styles.smooth);
        }, 300);
      } else {
        updateFadeState();
      }
    } else {
      // If no drag needed, reset position to 0
      if (currentLeft !== 0) {
        wrapper.classList.add(styles.smooth);
        wrapper.style.transform = 'translateX(0px)';

        setTimeout(() => {
          wrapper.classList.remove(styles.smooth);
        }, 300);
      }
    }

    // Update glider after potential position changes
    updateGlider();
  }, [checkOverflow, updateFadeState, updateGlider, getTranslateXValue]);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!dragState.canDrag) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const wrapper = tabWrapperRef.current;

    if (!wrapper) return;

    const currentLeft = getTranslateXValue(wrapper);

    setDragState(prev => ({
      ...prev,
      startX: clientX,
      startLeft: currentLeft,
      isDragging: false,
      dragStarted: false
    }));

    wrapper.style.transition = '';
    e.preventDefault();
  }, [dragState.canDrag, getTranslateXValue]);

  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (dragState.startX === 0) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragState.startX;
    const wrapper = tabWrapperRef.current;
    const container = navListRef.current;

    if (!wrapper || !container) return;

    // Check if we've moved enough to consider this a drag
    if (!dragState.dragStarted && Math.abs(deltaX) > DRAG_THRESHOLD) {
      setDragState(prev => ({
        ...prev,
        dragStarted: true,
        isDragging: true
      }));
    }

    if (dragState.dragStarted) {
      const newLeft = dragState.startLeft + deltaX;
      const availableWidth = container.parentElement!.clientWidth;
      const wrapperWidth = wrapper.scrollWidth;
      const maxLeft = -(wrapperWidth - availableWidth);

      const constrainedLeft = Math.max(maxLeft, Math.min(0, newLeft));
      const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);

      wrapper.style.transform = `translateX(${constrainedLeft ? constrainedLeft - wrapperGap : constrainedLeft}px)`;
      updateFadeState();
    }

    if (dragState.dragStarted) {
      e.preventDefault();
    }
  }, [dragState.startX, dragState.startLeft, dragState.dragStarted, DRAG_THRESHOLD, updateFadeState]);

  const handleDragEnd = useCallback(() => {
    if (dragState.startX === 0) return;

    const wrapper = tabWrapperRef.current;
    if (wrapper) {
      wrapper.classList.add(styles.smooth);
      setTimeout(() => {
        if (wrapper) wrapper.classList.remove(styles.smooth);
      }, 300);
    }

    // If a drag was in progress, set justDragged to true
    if (dragState.dragStarted) {
      justDragged.current = true;
      // Reset justDragged after a short delay to avoid blocking real clicks
      setTimeout(() => {
        justDragged.current = false;
      }, 100);
    }

    setDragState(prev => ({
      ...prev,
      startX: 0,
      isDragging: false,
      dragStarted: false
    }));
  }, [dragState.startX, dragState.dragStarted]);

  const handleTabClickWithDrag = useCallback((e: React.MouseEvent<HTMLLIElement>, index: number, label?: string) => {
    // Prevent tab activation if we just finished dragging
    if (dragState.isDragging || justDragged.current) {
      e.preventDefault();
      justDragged.current = false; // Reset after blocking
      return;
    }

    handleTabClick(e, index, label);
  }, [dragState.isDragging, handleTabClick]);

  useEffect(() => {
    resizeObserverRef.current?.disconnect();

    resizeObserverRef.current = new ResizeObserver(() => {
      handleResize();
    });

    const activeBtn = buttonRefs.current[activeTab];
    const navList = navListRef.current;

    if (activeBtn) {
      resizeObserverRef.current.observe(activeBtn);
    }
    if (navList) {
      resizeObserverRef.current.observe(navList);
    }

    handleResize();

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [activeTab, content, handleResize]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: TouchEvent) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (!content || content.length === 0) return;

    requestAnimationFrame(() => {
      checkOverflow();
      updateGlider();
    });
  }, [content, activeTab, checkOverflow, updateGlider]);

  // Scroll active tab into view if needed
  useEffect(() => {
    if (!dragState.canDrag) return;
    const wrapper = tabWrapperRef.current;
    const container = navListRef.current;
    const activeBtn = buttonRefs.current[activeTab];
    if (!wrapper || !container || !activeBtn) return;

    const containerRect = container.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    const currentLeft = getTranslateXValue(wrapper);

    const containerLeft = containerRect.left;
    const containerRight = containerRect.right;
    const btnLeft = btnRect.left;
    const btnRight = btnRect.right;

    let newLeft = currentLeft;
    if (btnLeft < containerLeft) newLeft += containerLeft - btnLeft;
    else if (btnRight > containerRight) newLeft -= btnRight - containerRight;

    const availableWidth = container.parentElement!.clientWidth;
    const wrapperWidth = wrapper.scrollWidth;
    const maxLeft = -(wrapperWidth - availableWidth);
    newLeft = Math.max(maxLeft, Math.min(0, newLeft));

    if (newLeft !== currentLeft) {
      const wrapperGap = parseInt(window.getComputedStyle(wrapper).gap);

      wrapper.classList.add(styles.smooth);
      wrapper.style.transform = `translateX(${newLeft ? newLeft - wrapperGap : newLeft}px)`;
      setTimeout(() => {
        wrapper.classList.remove(styles.smooth);
      }, 300);
      updateFadeState();
    }
  }, [activeTab, dragState.canDrag, updateFadeState, getTranslateXValue]);

  if (!content) return null;

  return (
    <div className={cn(styles.tabsNavigation, styles[`padding-top-${paddingTop}`], styles[`padding-bottom-${paddingBottom}`])}>
      <nav className={styles.tabsNavWrapper} role="tablist" aria-label="Tabs">
        <div
          className={cn(styles.tabsContainer, {
            [styles.draggable]: dragState.canDrag,
            [styles.showLeftFade]: dragState.showLeftFade,
            [styles.showRightFade]: dragState.showRightFade
          })}
          ref={navListRef}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            cursor: dragState.canDrag ? 'grab' : 'default',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <ul
            ref={tabWrapperRef}
            className={cn(styles.tabsNavList, styles[`gap-${tab_button_size}`], {
              [styles.smooth]: false
            })}
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none'
            }}
          >
            {content.map((tab, index) => (
              <li
                key={tab?.fields?.tab_label || index}
                onClick={(e) => handleTabClickWithDrag(e, index, tab?.fields?.tab_label)}
                className={cn(styles.tabNavItem, button_style, {
                  [styles.active]: pendingTab === index
                })}
                style={{ flexShrink: 0 }}
              >
                <button
                  type="button"
                  ref={(el) => {
                    buttonRefs.current[index] = el;
                  }}
                  className={cn(styles.tabNavButton, styles[`tab-${tab_button_size}`], button_style)}
                  role="tab"
                  aria-selected={activeTab === index}
                  aria-controls={`tabpanel-${index}`}
                  id={`tab-${index}`}
                  tabIndex={0}
                >
                  {tab?.fields?.tab_label}
                </button>
              </li>
            ))}

            <li
              className={cn(styles.glider, button_style)}
              style={{
                width: gliderStyle.width,
                transform: `translateX(${gliderStyle.left}px)`,
                position: 'absolute',
                pointerEvents: 'none'
              }}
            />
          </ul>
        </div>
      </nav>
    </div>
  );
};

export default TabsNavigation;
